import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  BarChart3,
  Boxes,
  Download,
  Flame,
  GitBranch,
  LayoutDashboard,
  Lock,
  ScrollText,
  Search,
  Settings as SettingsIcon,
  Shield,
  Users as UsersIcon,
  Zap,
  FileWarning,
  Monitor,
} from 'lucide-react';
import { cn } from '@/components/ui';
import { useCommandPaletteStore } from '@/store/commandPalette.store';
import { useDetections } from '@/api/detections';
import { useEndpoints } from '@/api/endpoints';
import { endpointDisplayName } from '@/lib/endpoint-display';

// ── Navigation items ──────────────────────────────────────────────────────────

interface NavItem {
  label: string;
  path: string;
  group: string;
  icon: React.ReactNode;
}

const NAV_ITEMS: NavItem[] = [
  { label: 'Command Center', path: '/dashboard',     group: 'Operations', icon: <LayoutDashboard className="h-3.5 w-3.5" /> },
  { label: 'Endpoints',      path: '/endpoints',     group: 'Operations', icon: <Boxes          className="h-3.5 w-3.5" /> },
  { label: 'Agent Deploy',   path: '/agent-deploy',  group: 'Operations', icon: <Download       className="h-3.5 w-3.5" /> },
  { label: 'AI Sessions',    path: '/ai-sessions',   group: 'Analysis',   icon: <Activity       className="h-3.5 w-3.5" /> },
  { label: 'Process Trace',  path: '/process-trace', group: 'Analysis',   icon: <GitBranch      className="h-3.5 w-3.5" /> },
  { label: 'Threats',        path: '/threats',       group: 'Analysis',   icon: <Flame          className="h-3.5 w-3.5" /> },
  { label: 'Threat Intel',   path: '/threat-intel',  group: 'Analysis',   icon: <Search         className="h-3.5 w-3.5" /> },
  { label: 'Analytics',      path: '/analytics',     group: 'Analysis',   icon: <BarChart3      className="h-3.5 w-3.5" /> },
  { label: 'Compliance',     path: '/compliance',    group: 'Governance', icon: <Lock           className="h-3.5 w-3.5" /> },
  { label: 'Policies',       path: '/policies',      group: 'Governance', icon: <Shield         className="h-3.5 w-3.5" /> },
  { label: 'Mitigation',     path: '/mitigation',    group: 'Governance', icon: <Zap            className="h-3.5 w-3.5" /> },
  { label: 'Forensic Logs',  path: '/audit-log',     group: 'Governance', icon: <ScrollText     className="h-3.5 w-3.5" /> },
  { label: 'Settings',       path: '/settings',      group: 'Governance', icon: <SettingsIcon   className="h-3.5 w-3.5" /> },
  { label: 'Users',          path: '/users',         group: 'Governance', icon: <UsersIcon      className="h-3.5 w-3.5" /> },
];

const MAX_PER_GROUP = 4;

// ── Types for flattened result list ──────────────────────────────────────────

type ResultKind = 'page' | 'detection' | 'endpoint';

interface PaletteResult {
  id: string;
  kind: ResultKind;
  label: string;
  sub?: string;
  path: string;
  icon: React.ReactNode;
}

// ── Main component ────────────────────────────────────────────────────────────

export function CommandPalette() {
  const { isOpen, close } = useCommandPaletteStore();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // ── Data ──────────────────────────────────────────────────────────────────
  const { data: detectionsPage } = useDetections({ limit: 200 });
  const { data: endpointsPage } = useEndpoints({ limit: 200, excludeUninstalled: true });

  // ── Open/close effects ────────────────────────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setActiveIndex(0);
      // Focus after portal renders
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [isOpen]);

  // Global Ctrl+K / Cmd+K listener
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        useCommandPaletteStore.getState().toggle();
      }
      if (e.key === 'Escape' && useCommandPaletteStore.getState().isOpen) {
        close();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [close]);

  // ── Filtered results ──────────────────────────────────────────────────────
  const q = query.toLowerCase();

  const pageResults: PaletteResult[] = NAV_ITEMS
    .filter((n) => n.label.toLowerCase().includes(q))
    .slice(0, MAX_PER_GROUP)
    .map((n) => ({
      id: `page:${n.path}`,
      kind: 'page',
      label: n.label,
      sub: n.group,
      path: n.path,
      icon: n.icon,
    }));

  const detectionResults: PaletteResult[] = (detectionsPage?.data ?? [])
    .filter((d) => {
      const label = (d.ruleName ?? d.description ?? '').toLowerCase();
      return label.includes(q) || (d.endpointHostname ?? '').toLowerCase().includes(q);
    })
    .slice(0, MAX_PER_GROUP)
    .map((d) => ({
      id: `detection:${d.id}`,
      kind: 'detection',
      label: d.ruleName ?? d.description ?? '—',
      sub: d.endpointAlias?.trim() || d.endpointHostname,
      path: '/threats',
      icon: <FileWarning className="h-3.5 w-3.5" />,
    }));

  const endpointResults: PaletteResult[] = (endpointsPage?.data ?? [])
    .filter((e) => e.hostname.toLowerCase().includes(q))
    .slice(0, MAX_PER_GROUP)
    .map((e) => ({
      id: `endpoint:${e.id}`,
      kind: 'endpoint',
      label: endpointDisplayName(e),
      sub: e.ipAddress,
      path: '/endpoints',
      icon: <Monitor className="h-3.5 w-3.5" />,
    }));

  const allResults = [...pageResults, ...detectionResults, ...endpointResults];

  // ── Keyboard navigation ───────────────────────────────────────────────────
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, allResults.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        const result = allResults[activeIndex];
        if (result) selectResult(result);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allResults, activeIndex],
  );

  // Reset active index when results change
  useEffect(() => {
    setActiveIndex(0);
  }, [query]);

  // Scroll active item into view
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-index="${activeIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  // ── Selection ─────────────────────────────────────────────────────────────
  const selectResult = useCallback(
    (result: PaletteResult) => {
      navigate(result.path);
      close();
    },
    [navigate, close],
  );

  // ── Nothing to render when closed ─────────────────────────────────────────
  if (!isOpen) return null;

  // ── Section rendering helper ───────────────────────────────────────────────
  const sections: { title: string; results: PaletteResult[] }[] = [
    { title: 'Pages', results: pageResults },
    { title: 'Detections', results: detectionResults },
    { title: 'Endpoints', results: endpointResults },
  ];

  let globalIndex = 0;

  const portal = (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[12vh] bg-black/60 backdrop-blur-sm"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) close();
      }}
    >
      <div
        className={cn(
          'w-full max-w-[560px] mx-4 bg-surface-2 border border-border-default rounded-lg shadow-2xl overflow-hidden',
        )}
      >
        {/* Input */}
        <div className="flex items-center gap-2 px-3 border-b border-border-default">
          <Search className="h-4 w-4 shrink-0 text-text-tertiary" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search pages, detections, endpoints…"
            className={cn(
              'flex-1 h-11 bg-transparent text-sm text-text-primary placeholder:text-text-tertiary',
              'outline-none border-none focus:ring-0',
            )}
            autoComplete="off"
            spellCheck={false}
          />
          <kbd className="shrink-0 rounded border border-border-subtle bg-surface-1 px-1.5 py-0.5 text-[10px] text-text-tertiary">
            ESC
          </kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[400px] overflow-y-auto py-1.5">
          {allResults.length === 0 ? (
            <p className="py-10 text-center text-sm text-text-tertiary">No results</p>
          ) : (
            sections.map(({ title, results }) => {
              if (results.length === 0) return null;
              return (
                <div key={title}>
                  <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-text-tertiary">
                    {title}
                  </div>
                  {results.map((result) => {
                    const idx = globalIndex++;
                    const isActive = idx === activeIndex;
                    return (
                      <button
                        key={result.id}
                        data-index={idx}
                        onMouseEnter={() => setActiveIndex(idx)}
                        onClick={() => selectResult(result)}
                        className={cn(
                          'flex w-full items-center gap-3 px-3 py-2 text-left text-sm transition-colors',
                          isActive
                            ? 'bg-accent/10 text-accent'
                            : 'text-text-secondary hover:bg-surface-1',
                        )}
                      >
                        <span className={cn('shrink-0', isActive ? 'text-accent' : 'text-text-tertiary')}>
                          {result.icon}
                        </span>
                        <span className="flex-1 truncate font-medium" title={result.label}>{result.label}</span>
                        {result.sub && (
                          <span className="shrink-0 text-xs text-text-tertiary">{result.sub}</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );

  return createPortal(portal, document.body);
}
